import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Globe,
  Play,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Sparkles,
  RefreshCw,
  ChevronDown,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  ArrowLeft,
} from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { tiktokIntelService } from '@/services/tiktok-intel'
import type {
  TikTokCrawlJob,
  TikTokCreative,
  CreateTikTokCrawlRequest,
  TikTokCrawlStage,
} from '@/services/tiktok-intel/types'

type View = 'form' | 'pipeline' | 'results' | 'detail'

export default function TikTokIntelPage() {
  const [view, setView] = useState<View>('form')
  const [currentJob, setCurrentJob] = useState<TikTokCrawlJob | null>(null)
  const [recentJobs, setRecentJobs] = useState<TikTokCrawlJob[]>([])
  const [creatives, setCreatives] = useState<TikTokCreative[]>([])
  const [selectedCreative, setSelectedCreative] = useState<TikTokCreative | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasInitialized = useRef(false)

  // Form state
  const [keyword, setKeyword] = useState('')
  const [advertiser, setAdvertiser] = useState('')
  const [region, setRegion] = useState('')
  const [maxResults, setMaxResults] = useState(20)

  // Load recent jobs on mount
  const loadRecentJobs = useCallback(async () => {
    try {
      const response = await tiktokIntelService.listCrawlJobs()
      const jobs = response.jobs || []
      setRecentJobs(jobs)

      // Auto-resume if there's a running job
      const runningJob = jobs.find(j => j.status === 'running' || j.status === 'pending')
      if (runningJob) {
        setCurrentJob(runningJob)
        setView('pipeline')
        return
      }

      // Show results of most recent completed job
      const completedJob = jobs.find(j => j.status === 'completed')
      if (completedJob && completedJob.normalized_count > 0) {
        setCurrentJob(completedJob)
        await loadCreatives(completedJob.job_id)
        setView('results')
        return
      }
    } catch (err) {
      console.warn('[TikTokIntel] Failed to load jobs:', err)
    }
  }, [])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      loadRecentJobs()
    }
  }, [loadRecentJobs])

  // Poll for job updates when in pipeline view
  useEffect(() => {
    if (view === 'pipeline' && currentJob && (currentJob.status === 'running' || currentJob.status === 'pending')) {
      pollRef.current = setInterval(async () => {
        try {
          const updated = await tiktokIntelService.getCrawlJob(currentJob.job_id)
          setCurrentJob(updated)

          if (updated.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current)
            await loadCreatives(updated.job_id)
            setView('results')
          } else if (updated.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current)
            setError(updated.error_message || 'Crawl failed')
          }
        } catch (err) {
          console.error('[TikTokIntel] Poll error:', err)
        }
      }, 3000)

      return () => {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [view, currentJob?.job_id, currentJob?.status])

  const loadCreatives = async (jobId: string) => {
    try {
      const response = await tiktokIntelService.listCreatives(jobId)
      setCreatives(response.creatives || [])
    } catch (err) {
      console.error('[TikTokIntel] Failed to load creatives:', err)
    }
  }

  const handleSubmit = async () => {
    if (!keyword && !advertiser) {
      setError('Enter a keyword or advertiser name')
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      const request: CreateTikTokCrawlRequest = {
        keyword: keyword || undefined,
        advertiser: advertiser || undefined,
        region: region || undefined,
        max_results: maxResults,
      }

      const job = await tiktokIntelService.createCrawlJob(request)
      setCurrentJob(job)
      setView('pipeline')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start crawl')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleViewCreative = async (creative: TikTokCreative) => {
    try {
      const full = await tiktokIntelService.getCreative(creative.creative_id)
      setSelectedCreative(full)
      setView('detail')
    } catch {
      setSelectedCreative(creative)
      setView('detail')
    }
  }

  const handleReEnrich = async (creativeId: string) => {
    try {
      const updated = await tiktokIntelService.reEnrichCreative(creativeId)
      setSelectedCreative(updated)
      // Update in list too
      setCreatives(prev => prev.map(c => c.creative_id === creativeId ? updated : c))
    } catch (err) {
      console.error('[TikTokIntel] Re-enrich failed:', err)
    }
  }

  const handleNewSearch = () => {
    setCurrentJob(null)
    setCreatives([])
    setSelectedCreative(null)
    setError(null)
    setKeyword('')
    setAdvertiser('')
    setRegion('')
    setMaxResults(20)
    setView('form')
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Globe className="w-6 h-6 text-cyan-400" />
              TikTok Ad Intelligence
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Public competitive intelligence from TikTok ads
            </p>
          </div>
          {view !== 'form' && (
            <button
              onClick={handleNewSearch}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 transition-colors"
            >
              <Search className="w-4 h-4" />
              New Search
            </button>
          )}
        </div>

        <AnimatePresence mode="wait">
          {view === 'form' && (
            <CrawlForm
              keyword={keyword}
              setKeyword={setKeyword}
              advertiser={advertiser}
              setAdvertiser={setAdvertiser}
              region={region}
              setRegion={setRegion}
              maxResults={maxResults}
              setMaxResults={setMaxResults}
              isSubmitting={isSubmitting}
              error={error}
              onSubmit={handleSubmit}
              recentJobs={recentJobs}
              onResumeJob={(job) => {
                setCurrentJob(job)
                if (job.status === 'completed') {
                  loadCreatives(job.job_id).then(() => setView('results'))
                } else {
                  setView('pipeline')
                }
              }}
            />
          )}

          {view === 'pipeline' && currentJob && (
            <CrawlPipeline job={currentJob} error={error} />
          )}

          {view === 'results' && (
            <CreativeResults
              job={currentJob}
              creatives={creatives}
              onViewCreative={handleViewCreative}
            />
          )}

          {view === 'detail' && selectedCreative && (
            <CreativeDetail
              creative={selectedCreative}
              onBack={() => setView('results')}
              onReEnrich={handleReEnrich}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Crawl Form
// ──────────────────────────────────────────────────────────────────────────────

interface CrawlFormProps {
  keyword: string
  setKeyword: (v: string) => void
  advertiser: string
  setAdvertiser: (v: string) => void
  region: string
  setRegion: (v: string) => void
  maxResults: number
  setMaxResults: (v: number) => void
  isSubmitting: boolean
  error: string | null
  onSubmit: () => void
  recentJobs: TikTokCrawlJob[]
  onResumeJob: (job: TikTokCrawlJob) => void
}

function CrawlForm({
  keyword, setKeyword, advertiser, setAdvertiser,
  region, setRegion, maxResults, setMaxResults,
  isSubmitting, error, onSubmit, recentJobs, onResumeJob,
}: CrawlFormProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Search Form */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Search TikTok Ads</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Keyword</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g., skincare, fitness app, meal kit"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 outline-none transition-colors"
              onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Advertiser / Brand</label>
            <input
              type="text"
              value={advertiser}
              onChange={(e) => setAdvertiser(e.target.value)}
              placeholder="e.g., @gymshark, @theordinary"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 outline-none transition-colors"
              onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Region</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white outline-none transition-colors"
            >
              <option value="">All regions</option>
              <option value="US">United States</option>
              <option value="GB">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="BR">Brazil</option>
              <option value="MX">Mexico</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Max Results</label>
            <select
              value={maxResults}
              onChange={(e) => setMaxResults(Number(e.target.value))}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white outline-none transition-colors"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={onSubmit}
          disabled={isSubmitting || (!keyword && !advertiser)}
          className={cn(
            'mt-4 w-full py-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2',
            isSubmitting || (!keyword && !advertiser)
              ? 'bg-white/5 text-gray-500 cursor-not-allowed'
              : 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg shadow-cyan-600/20'
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Starting crawl...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Start TikTok Crawl
            </>
          )}
        </button>
      </div>

      {/* Recent Jobs */}
      {recentJobs.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Recent Crawls</h3>
          <div className="space-y-2">
            {recentJobs.slice(0, 5).map((job) => (
              <button
                key={job.job_id}
                onClick={() => onResumeJob(job)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <StatusBadge status={job.status} />
                  <div>
                    <div className="text-sm text-white">
                      {job.input?.keyword || job.input?.advertiser || 'Crawl'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(job.created_at).toLocaleDateString()} &middot; {job.normalized_count} creatives
                    </div>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500 -rotate-90" />
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Crawl Pipeline (progress view)
// ──────────────────────────────────────────────────────────────────────────────

function CrawlPipeline({ job, error }: { job: TikTokCrawlJob; error: string | null }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Progress header */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {job.input?.keyword || job.input?.advertiser || 'TikTok Crawl'}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {job.status === 'running' ? 'Crawling TikTok ads...' : job.status}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-cyan-400">{job.progress_pct}%</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400"
            initial={{ width: 0 }}
            animate={{ width: `${job.progress_pct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Stage cards */}
      <div className="grid gap-3">
        {job.stages.map((stage) => (
          <StageRow key={stage.name} stage={stage} />
        ))}
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}
    </motion.div>
  )
}

function StageRow({ stage }: { stage: TikTokCrawlStage }) {
  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-gray-500" />,
    running: <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />,
    completed: <CheckCircle2 className="w-4 h-4 text-green-400" />,
    degraded: <AlertCircle className="w-4 h-4 text-amber-400" />,
    failed: <AlertCircle className="w-4 h-4 text-red-400" />,
    skipped: <Clock className="w-4 h-4 text-gray-600" />,
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg transition-colors',
        stage.status === 'running' ? 'bg-cyan-500/5 border border-cyan-500/20' : 'bg-white/[0.02] border border-white/5'
      )}
    >
      {statusIcon[stage.status]}
      <div className="flex-1">
        <div className={cn(
          'text-sm font-medium',
          stage.status === 'running' ? 'text-white' : stage.status === 'pending' ? 'text-gray-500' : 'text-gray-300'
        )}>
          {stage.display_name}
        </div>
        {stage.insight && (
          <div className="text-xs text-gray-500 mt-0.5">{stage.insight}</div>
        )}
      </div>
      {stage.duration_ms !== undefined && stage.duration_ms > 0 && (
        <div className="text-xs text-gray-500">
          {(stage.duration_ms / 1000).toFixed(1)}s
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Creative Results Grid
// ──────────────────────────────────────────────────────────────────────────────

function CreativeResults({
  job, creatives, onViewCreative,
}: {
  job: TikTokCrawlJob | null
  creatives: TikTokCreative[]
  onViewCreative: (c: TikTokCreative) => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Summary bar */}
      {job && (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="px-3 py-1.5 rounded-lg bg-white/5 text-sm text-gray-300">
            <span className="text-white font-medium">{job.normalized_count}</span> creatives found
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-white/5 text-sm text-gray-300">
            <span className="text-white font-medium">{job.enriched_count}</span> AI-enriched
          </div>
          {job.duplicates_skipped > 0 && (
            <div className="px-3 py-1.5 rounded-lg bg-white/5 text-sm text-gray-300">
              <span className="text-gray-400">{job.duplicates_skipped}</span> duplicates skipped
            </div>
          )}
          {job.input?.keyword && (
            <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 text-sm text-cyan-300">
              keyword: {job.input.keyword}
            </div>
          )}
          {job.input?.advertiser && (
            <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 text-sm text-cyan-300">
              advertiser: {job.input.advertiser}
            </div>
          )}
        </div>
      )}

      {/* Results grid */}
      {creatives.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          No creatives found for this crawl.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {creatives.map((creative) => (
            <CreativeCard
              key={creative.creative_id}
              creative={creative}
              onClick={() => onViewCreative(creative)}
            />
          ))}
        </div>
      )}
    </motion.div>
  )
}

function CreativeCard({ creative, onClick }: { creative: TikTokCreative; onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      className="text-left rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-cyan-500/30 transition-colors"
    >
      {/* Thumbnail / Video preview */}
      <div className="aspect-video bg-black/50 relative overflow-hidden">
        {creative.thumbnail_url ? (
          <img
            src={creative.thumbnail_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="w-10 h-10 text-gray-600" />
          </div>
        )}
        {creative.video_url && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Play className="w-5 h-5 text-white ml-0.5" />
            </div>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Advertiser */}
        {creative.advertiser_name && (
          <div className="text-xs font-medium text-cyan-400 truncate">
            @{creative.advertiser_name}
          </div>
        )}

        {/* Ad text */}
        {creative.ad_text && (
          <p className="text-sm text-gray-300 line-clamp-2">{creative.ad_text}</p>
        )}

        {/* Metrics row */}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {(creative.view_count ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" /> {formatCount(creative.view_count!)}
            </span>
          )}
          {(creative.like_count ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" /> {formatCount(creative.like_count!)}
            </span>
          )}
          {(creative.comment_count ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> {formatCount(creative.comment_count!)}
            </span>
          )}
          {(creative.share_count ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <Share2 className="w-3 h-3" /> {formatCount(creative.share_count!)}
            </span>
          )}
        </div>

        {/* Enrichment tags */}
        {creative.enrichment && (
          <div className="flex flex-wrap gap-1.5">
            {creative.enrichment.content_style && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/10 text-purple-400 uppercase">
                {creative.enrichment.content_style}
              </span>
            )}
            {creative.enrichment.creative_angle && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-500/10 text-cyan-400">
                {creative.enrichment.creative_angle}
              </span>
            )}
            {creative.enrichment.style_tags?.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/5 text-gray-400"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.button>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Creative Detail View
// ──────────────────────────────────────────────────────────────────────────────

function CreativeDetail({
  creative, onBack, onReEnrich,
}: {
  creative: TikTokCreative
  onBack: () => void
  onReEnrich: (id: string) => void
}) {
  const [isEnriching, setIsEnriching] = useState(false)

  const handleReEnrich = async () => {
    setIsEnriching(true)
    await onReEnrich(creative.creative_id)
    setIsEnriching(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to results
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Video / Preview */}
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 overflow-hidden bg-black">
            {creative.video_url ? (
              <video
                src={creative.video_url}
                controls
                className="w-full aspect-[9/16] max-h-[480px] object-contain"
                poster={creative.thumbnail_url}
              />
            ) : creative.thumbnail_url ? (
              <img
                src={creative.thumbnail_url}
                alt=""
                className="w-full aspect-video object-cover"
              />
            ) : (
              <div className="w-full aspect-video flex items-center justify-center text-gray-600">
                <Play className="w-16 h-16" />
              </div>
            )}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-4 gap-3">
            <MetricBox icon={<Eye className="w-4 h-4" />} label="Views" value={creative.view_count} />
            <MetricBox icon={<Heart className="w-4 h-4" />} label="Likes" value={creative.like_count} />
            <MetricBox icon={<MessageCircle className="w-4 h-4" />} label="Comments" value={creative.comment_count} />
            <MetricBox icon={<Share2 className="w-4 h-4" />} label="Shares" value={creative.share_count} />
          </div>
        </div>

        {/* Right: Details + Enrichment */}
        <div className="space-y-4">
          {/* Basic info */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
            <h2 className="text-lg font-semibold text-white">Creative Details</h2>

            <DetailRow label="Advertiser" value={creative.advertiser_name} />
            <DetailRow label="Platform" value="TikTok" />
            <DetailRow label="Region" value={creative.region || creative.country} />
            <DetailRow label="Industry" value={creative.industry} />
            <DetailRow label="Format" value={creative.ad_format} />

            {creative.ad_text && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Ad Copy</div>
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{creative.ad_text}</p>
              </div>
            )}

            {creative.cta && <DetailRow label="CTA" value={creative.cta} />}

            {creative.landing_page_url && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Landing Page</div>
                <a
                  href={creative.landing_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cyan-400 hover:underline break-all"
                >
                  {creative.landing_page_url}
                </a>
              </div>
            )}
          </div>

          {/* AI Enrichment */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                AI Analysis
              </h3>
              <button
                onClick={handleReEnrich}
                disabled={isEnriching}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <RefreshCw className={cn('w-3 h-3', isEnriching && 'animate-spin')} />
                {isEnriching ? 'Analyzing...' : 'Re-analyze'}
              </button>
            </div>

            {creative.enrichment ? (
              <div className="space-y-3">
                <DetailRow label="Hook" value={creative.enrichment.hook} />
                <DetailRow label="Creative Angle" value={creative.enrichment.creative_angle} />
                <DetailRow label="Content Style" value={creative.enrichment.content_style} />
                <DetailRow label="Product Detected" value={creative.enrichment.product_detected} />
                <DetailRow label="Offer Detected" value={creative.enrichment.offer_detected} />
                <DetailRow label="Target Persona" value={creative.enrichment.target_persona} />
                <DetailRow label="CTA Analysis" value={creative.enrichment.cta_analysis} />

                {creative.enrichment.winning_patterns && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Winning Patterns</div>
                    <p className="text-sm text-gray-300">{creative.enrichment.winning_patterns}</p>
                  </div>
                )}

                {creative.enrichment.style_tags && creative.enrichment.style_tags.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">Style Tags</div>
                    <div className="flex flex-wrap gap-1.5">
                      {creative.enrichment.style_tags.map((tag) => (
                        <span key={tag} className="px-2 py-1 rounded-full text-xs bg-white/5 text-gray-300">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {creative.enrichment.emotional_triggers && creative.enrichment.emotional_triggers.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">Emotional Triggers</div>
                    <div className="flex flex-wrap gap-1.5">
                      {creative.enrichment.emotional_triggers.map((trigger) => (
                        <span key={trigger} className="px-2 py-1 rounded-full text-xs bg-amber-500/10 text-amber-400">
                          {trigger}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No AI analysis yet. Click "Re-analyze" to generate.
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared UI Components
// ──────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Pending' },
    running: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Running' },
    completed: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Done' },
    failed: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Failed' },
    cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Cancelled' },
  }
  const c = config[status] || config.pending
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium', c.bg, c.text)}>
      {c.label}
    </span>
  )
}

function MetricBox({ icon, label, value }: { icon: React.ReactNode; label: string; value?: number }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 text-center">
      <div className="flex justify-center text-gray-500 mb-1">{icon}</div>
      <div className="text-lg font-semibold text-white">{formatCount(value || 0)}</div>
      <div className="text-[10px] text-gray-500 uppercase">{label}</div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm text-gray-300">{value}</div>
    </div>
  )
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}
